import axios from "axios";
import { extractErrorMessage } from "@/helpers/apiErrorHelper";

// The driving range has exactly three draggable points, keyed the same way by
// every endpoint. Every response returns [latitude, longitude]; the update
// request body is the one place that takes [longitude, latitude].
const PIN_DEFINITIONS = [
  { key: "front", name: "Front" },
  { key: "center", name: "Center" },
  { key: "back", name: "Back" },
];

// Leaflet wants [latitude, longitude], so that is what a pin holds.
const toPoint = (value) => {
  if (!Array.isArray(value) || value.length < 2) return null;

  const latitude = parseFloat(value[0]);
  const longitude = parseFloat(value[1]);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;

  return [latitude, longitude];
};

// Build the pin list from any response keyed by front/center/back, keeping the
// display order and dropping anything without usable coordinates.
const mapPins = (source) => {
  if (!source) return [];

  return PIN_DEFINITIONS.reduce((pins, definition) => {
    const point = toPoint(source[definition.key]);

    if (point) {
      pins.push({ ...definition, point });
    }

    return pins;
  }, []);
};

// Dragging a marker yields full floating point precision, while every stored
// coordinate uses six decimals. Six is roughly 0.1m on the ground, so round to
// match rather than sending a value in a shape the backend never stores.
const COORDINATE_DECIMALS = 6;

const roundCoordinate = (value) => {
  return Number(value.toFixed(COORDINATE_DECIMALS));
};

const buildUpdatePayload = (rangePins) => {
  return rangePins.reduce((body, pin) => {
    if (Array.isArray(pin.point)) {
      const [latitude, longitude] = pin.point;
      // The update endpoint expects longitude first.
      body[pin.key] = [roundCoordinate(longitude), roundCoordinate(latitude)];
    }

    return body;
  }, {});
};

const state = () => ({
  rangePins: [],
  drivingRangeSetup: null,
  selectedCourse: null,
  loading: false,
});

const mutations = {
  SET_RANGE_PINS(state, rangePins) {
    state.rangePins = rangePins;
  },
  SET_DRIVING_RANGE_SETUP(state, drivingRangeSetup) {
    state.drivingRangeSetup = drivingRangeSetup;
  },
  SET_SELECTED_COURSE(state, course) {
    state.selectedCourse = course;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
};

const getters = {
  getRangePins: (state) => {
    return state.rangePins;
  },
  getDrivingRangeSetup: (state) => {
    return state.drivingRangeSetup;
  },
  getSelectedCourse: (state) => {
    return state.selectedCourse;
  },
  getLoading: (state) => {
    return state.loading;
  },
};

const actions = {
  // Central fetches the PWA setup from the course's server and relays it, so the
  // browser never calls the course URL directly.
  async fetchPwaSetup({ commit, dispatch }, courseId) {
    const url =
      process.env.VUE_APP_API_URL +
      `/utilities/drivingRangePins/setup/${courseId}`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.get(url, config);
      const setup = response.data || null;
      const rangePins = mapPins(setup);

      commit("SET_DRIVING_RANGE_SETUP", setup);
      commit("SET_RANGE_PINS", rangePins);

      const warn = (message) => {
        dispatch(
          "toastNotification/displayToast",
          { message, type: "warning", duration: 7000 },
          { root: true }
        );
      };

      if (!setup) {
        warn("No driving range setup found for this course.");
      } else {
        if (rangePins.length === 0) {
          warn("No driving range pins found for this course.");
        }

        if (!setup.drivingRangeMap) {
          warn(
            "No driving range map found for this course. Showing satellite imagery only."
          );
        }
      }

      return setup;
    } catch (error) {
      commit("SET_DRIVING_RANGE_SETUP", null);
      commit("SET_RANGE_PINS", []);
      dispatch(
        "toastNotification/displayToast",
        {
          message: extractErrorMessage(
            error,
            "Failed to fetch driving range setup"
          ),
          type: "error",
        },
        { root: true }
      );
      throw error.response?.data;
    }
  },

  async updateDrivingRangePins({ dispatch, commit }, payload) {
    commit("SET_LOADING", true);
    const url =
      process.env.VUE_APP_API_URL +
      `/utilities/drivingRangePins/${payload.courseId}`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.patch(
        url,
        buildUpdatePayload(payload.rangePins),
        config
      );

      // The endpoint echoes the stored coordinates, so redraw from those
      // rather than from what was dragged.
      const updatedPins = mapPins(response.data?.pins ?? response.data);
      if (updatedPins.length) {
        commit("SET_RANGE_PINS", updatedPins);
      }

      dispatch(
        "toastNotification/displayToast",
        {
          message: `${
            response?.data?.message || "Driving range pins successfully updated"
          }`,
          type: "success",
        },
        { root: true }
      );

      return response.data;
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: extractErrorMessage(error, "Failed to update pins"),
          type: "error",
          duration: 6000,
        },
        { root: true }
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async importDrivingRangePins({ commit }, payload) {
    const url =
      process.env.VUE_APP_API_URL +
      `/utilities/drivingRangePins/import/${payload.courseId}`;

    const formData = new FormData();
    formData.append("uploaded_file", payload.file);

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
        "Content-Type": "multipart/form-data",
      },
    };

    const response = await axios.post(url, formData, config);

    // The import returns the stored coordinates, so redraw straight from the
    // response instead of refetching the setup.
    const importedPins = mapPins(response.data?.pins ?? response.data);
    if (importedPins.length) {
      commit("SET_RANGE_PINS", importedPins);
    }

    return response.data;
  },

  async clearRangePins({ dispatch, commit }, id) {
    commit("SET_LOADING", true);
    const url =
      process.env.VUE_APP_API_URL + `/utilities/range-pins/${id}/clear`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.delete(url, config);
      dispatch(
        "toastNotification/displayToast",
        {
          message: `${
            response?.data?.message || "Range pins cleared successfully."
          }`,
          type: "success",
        },
        { root: true }
      );
      commit("SET_RANGE_PINS", []);
      return response.data;
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: `${
            error?.response?.data?.message || "Failed to clear range pins"
          }`,
          type: "error",
        },
        { root: true }
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
